import type { ColumnDef } from '@tanstack/vue-table'
import { PenIcon } from 'lucide-vue-next'
import { Button } from '~/components/ui/button'
import DataTableFilterableHeader from '~/components/ui/table/DataTableFilterableHeader.vue'
import AssignmentLegendIndicator from '~/components/AssignmentLegendIndicator.vue'
import type { OptionItem } from '~/types'

export const createEsgColumns = (
  onRowSelect?: (row: EsgAssignmentTableRow) => void,
  filterOptions?: EsgDataAssignmentFilters,
  $modal?: { hide: (name: string) => void, show: (name: string) => void },
  onRowEdit?: (row: EsgAssignmentTableRow) => void,
  canEdit: boolean = true,
): ColumnDef<EsgAssignmentTableRow>[] => {
  // transform approval_types only since it has different structure
  const transformApprovalTypes = (items: Array<{ value: string, label: string }>): OptionItem[] =>
    items.map(item => ({ id: item.value, name: item.label }))

  const columns: ColumnDef<EsgAssignmentTableRow>[] = [
    {
      accessorKey: 'asset.asset_name',
      id: 'asset',
      header: ({ column }) => h(DataTableFilterableHeader, {
        column,
        title: 'Asset',
        filterData: filterOptions?.assets || [],
      }),
      cell: ({ row }) => {
        return h('span', { class: 'text-md text-theme-text-grey no-localize-translate' },
          row.original.asset_name,
        )
      },
    },
    {
      accessorKey: 'metric_name',
      id: 'metric',
      header: ({ column }) => h(DataTableFilterableHeader, {
        column,
        title: 'Metric',
        filterData: filterOptions?.metrics || [],
      }),
      cell: ({ row }) => {
        const metricName = row.original.metric_name
        const shouldTruncate = metricName && metricName.length > 25
        const tooltipClasses = 'absolute hidden group-hover:block -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50'

        return shouldTruncate
          ? h('div', { class: 'flex items-center relative group' }, [
            h(AssignmentLegendIndicator, {
              theme: row.original.theme,
              title: metricName.substring(0, 25) + '...',
            }),
            h('div', { class: tooltipClasses }, metricName),
          ])
          : h('div', { class: 'flex items-center' }, [
            h(AssignmentLegendIndicator, {
              theme: row.original.theme,
              title: metricName,
            }),
          ])
      },
    },
    {
      accessorKey: 'template_name',
      id: 'template',
      header: ({ column }) => h(DataTableFilterableHeader, {
        column,
        title: 'Template',
        filterData: filterOptions?.template_names || [],
      }),
      cell: ({ row }) => {
        const templateName = row.original.template_name
        const tooltipClasses = 'absolute hidden group-hover:block -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50'

        if (templateName === 'No Template Assigned') {
          return h('div', { class: 'relative group' }, [
            h('span', { class: 'text-md text-theme-text-grey cursor-pointer' },
              'No Template Assigned to Metric',
            ),
            h('div', { class: tooltipClasses }, 'Please head to ESG Metric Configuration to assign template'),
          ])
        }

        const shouldTruncate = templateName && templateName.length > 25

        return shouldTruncate
          ? h('div', { class: 'relative group' }, [
            h('span', { class: 'text-md text-theme-text-grey cursor-pointer' },
              templateName.substring(0, 25) + '...',
            ),
            h('div', { class: tooltipClasses }, templateName),
          ])
          : h('span', { class: 'text-md text-theme-text-grey' }, templateName)
      },
    },
    {
      accessorKey: 'approval_type',
      id: 'approval_type',
      header: ({ column }) => h(DataTableFilterableHeader, {
        column,
        title: 'Approval Type',
        filterData: filterOptions?.approval_types ? transformApprovalTypes(filterOptions.approval_types) : [],
      }),
      cell: ({ row }) => {
        const approvalType = row.original.approval_type
        let displayText = approvalType

        // Map approval types according to requirements
        if (approvalType?.toLowerCase() === 'n/a' || approvalType?.toLowerCase() === 'not_required') {
          displayText = 'Not Required'
        }
        else if (approvalType?.toLowerCase() === 'all_required' || approvalType?.toLowerCase() === 'all required') {
          displayText = 'All Required'
        }
        else if (approvalType?.toLowerCase() === 'one_required' || approvalType?.toLowerCase() === '1 approval required') {
          displayText = '1 Approval Required'
        }

        return h('span', { class: 'text-md text-theme-text-grey' }, displayText)
      },
    },
    {
      accessorKey: 'owners',
      id: 'data_owners',
      header: ({ column }) => h(DataTableFilterableHeader, {
        column,
        title: 'Data Owner',
        filterData: filterOptions?.data_owners || [],
      }),
      cell: ({ row }) => {
        const owners = row.original.owners
        if (!owners) return h('span', { class: 'text-md text-theme-text-grey' }, '-')

        const shouldTruncate = owners.length > 30
        const tooltipClasses = 'absolute hidden group-hover:block -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50'

        return shouldTruncate
          ? h('div', { class: 'relative group' }, [
            h('span', { class: 'text-md text-theme-text-grey cursor-pointer no-localize-translate' },
              owners.substring(0, 30) + '...',
            ),
            h('div', { class: `${tooltipClasses} no-localize-translate` }, owners),
          ])
          : h('span', { class: 'text-md text-theme-text-grey no-localize-translate' }, owners)
      },
    },
    {
      accessorKey: 'approver_list',
      id: 'data_approvers',
      header: ({ column }) => h(DataTableFilterableHeader, {
        column,
        title: 'Approver',
        filterData: filterOptions?.data_approvers || [],
      }),
      cell: ({ row }) => {
        const approvers = row.original.approver_list
        if (!approvers) return h('span', { class: 'text-md text-theme-text-grey' }, '-')

        const shouldTruncate = approvers.length > 30
        const tooltipClasses = 'absolute hidden group-hover:block -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50'

        return shouldTruncate
          ? h('div', { class: 'relative group' }, [
            h('span', { class: 'text-md text-theme-text-grey cursor-pointer no-localize-translate' },
              approvers.substring(0, 30) + '...',
            ),
            h('div', { class: `${tooltipClasses} no-localize-translate` }, approvers),
          ])
          : h('span', { class: 'text-md text-theme-text-grey no-localize-translate' }, approvers)
      },
    },
  ]

  // Conditionally add actions column if canEdit is true
  if (canEdit) {
    columns.push({
      id: 'actions',
      header: () => {
        return h('span', { class: 'text-sm font-semibold text-theme-text-black' }, 'Actions')
      },
      cell: ({ row }) => {
        return h(Button, {
          variant: 'ghost',
          size: 'sm',
          onClick: () => {
            if ($modal && onRowEdit) {
              onRowEdit(row.original)
              $modal.show('esgEditAssignmentModal')
            }
            else {
              onRowSelect?.(row.original)
            }
          },
        }, () => h(PenIcon, { class: 'size-5 text-main-primary' }))
      },
    })
  }

  return columns
}
